import React, { useMemo, useState } from "react";

import { DateRange } from "react-day-picker";
import { useNavigate } from "react-router-dom";

import {
  DollarSign,
  Download,
  FileText,
  Search,
  TrendingUp,
  Users,
} from "lucide-react";
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import { CalendarDateRangePicker } from "~/components/date-range-picker";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Progress } from "~/components/ui/progress";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useToast } from "~/hooks/use-toast";
import { useClientSalesDashboard } from "~/hooks/useClientSalesDashboard";
import { exportClientSalesToExcel } from "~/services/clientSalesExportService";
import { formatCurrency } from "~/utils/helpers";

const ClientSalesDashboardPage: React.FC = () => {
  const {
    clients,
    allClients,
    summary,
    isLoading,
    error,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    searchQuery,
    setSearchQuery,
    fetchDetailsForExport,
  } = useClientSalesDashboard();

  const { toast } = useToast();
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState(false);

  // Colors for the pie chart
  const CHART_COLORS = [
    "#2563eb", // blue-600
    "#16a34a", // green-600
    "#dc2626", // red-600
    "#ca8a04", // yellow-600
    "#9333ea", // purple-600
    "#0891b2", // cyan-600
    "#ea580c", // orange-600
    "#4f46e5", // indigo-600
    "#db2777", // pink-600
    "#65a30d", // lime-600
    "#6b7280", // gray-500 (for "Otros")
  ];

  // Prepare pie chart data (top 10 clients + others)
  const pieChartData = useMemo(() => {
    if (allClients.length === 0) return [];

    const TOP_N = 10;
    const topClients = allClients.slice(0, TOP_N);
    const otherClients = allClients.slice(TOP_N);

    const chartData = topClients.map((client) => ({
      name:
        client.clientName.length > 20
          ? client.clientName.substring(0, 20) + "..."
          : client.clientName,
      value: client.totalSales,
      percentage: client.percentage,
      clientId: client.clientId,
    }));

    // Add "Otros" if there are more clients
    if (otherClients.length > 0) {
      const othersTotal = otherClients.reduce(
        (sum, c) => sum + c.totalSales,
        0
      );
      const othersPercentage = otherClients.reduce(
        (sum, c) => sum + c.percentage,
        0
      );
      chartData.push({
        name: `Otros (${otherClients.length})`,
        value: othersTotal,
        percentage: othersPercentage,
        clientId: null,
      });
    }

    return chartData;
  }, [allClients]);

  const handlePieClick = (data: { clientId: string | null }) => {
    if (data.clientId) {
      navigate(`/clients/${data.clientId}`);
    }
  };

  const handleDateRangeChange = (range: DateRange | undefined) => {
    setStartDate(range?.from);
    setEndDate(range?.to);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      toast({
        title: "Generando reporte...",
        description: "Por favor espere mientras se genera el archivo Excel.",
      });

      const invoiceDetails = await fetchDetailsForExport();

      await exportClientSalesToExcel(
        allClients,
        invoiceDetails,
        startDate,
        endDate
      );

      toast({
        title: "Reporte generado",
        description: "El archivo Excel se ha descargado correctamente.",
      });
    } catch (err) {
      console.error("Error exporting:", err);
      toast({
        title: "Error",
        description: "No se pudo generar el reporte. Intente nuevamente.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (error) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-red-500">Error al cargar los datos: {error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto space-y-6 py-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Ventas por Cliente
          </h1>
          <p className="text-muted-foreground">
            Ranking de clientes por ventas facturadas
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <CalendarDateRangePicker
            dateRange={{ from: startDate, to: endDate }}
            onDateRangeChange={handleDateRangeChange}
          />
          <Button
            onClick={handleExport}
            disabled={isExporting || isLoading || clients.length === 0}
            variant="outline"
          >
            <Download className="mr-2 h-4 w-4" />
            {isExporting ? "Exportando..." : "Exportar Excel"}
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-4 rounded" />
              </CardHeader>
              <CardContent>
                <Skeleton className="mb-2 h-8 w-24" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card
            style={{ animationDelay: "0ms" }}
            className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Ventas Totales (Neto)
              </CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${formatCurrency(summary.totalSales)}
              </div>
            </CardContent>
          </Card>

          <Card
            style={{ animationDelay: "60ms" }}
            className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Facturas
              </CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.totalInvoices}</div>
            </CardContent>
          </Card>

          <Card
            style={{ animationDelay: "120ms" }}
            className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Clientes con Ventas
              </CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.totalClients}</div>
            </CardContent>
          </Card>

          <Card
            style={{ animationDelay: "180ms" }}
            className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Promedio por Cliente
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${formatCurrency(summary.averagePerClient)}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Pie Chart */}
      {!isLoading && pieChartData.length > 0 && (
        <Card
          style={{ animationDelay: "240ms" }}
          className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
        >
          <CardHeader>
            <CardTitle>Distribución de Ventas por Cliente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    onClick={(_, index) => handlePieClick(pieChartData[index])}
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={CHART_COLORS[index % CHART_COLORS.length]}
                        cursor={entry.clientId ? "pointer" : "default"}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number, _, entry) => [
                      `$${formatCurrency(value)} (${entry.payload.percentage.toFixed(1)}%)`,
                      entry.payload.name,
                    ]}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search and Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>Ranking de Clientes</CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar cliente o RUT..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : clients.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              {searchQuery
                ? "No se encontraron clientes con ese criterio de búsqueda."
                : "No hay datos de ventas para el período seleccionado."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>RUT</TableHead>
                    <TableHead className="text-right">Ventas Totales</TableHead>
                    <TableHead className="text-right"># Facturas</TableHead>
                    <TableHead className="w-48">% del Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clients.map((client, index) => (
                    <TableRow
                      key={client.clientId}
                      style={{
                        animationDelay: `${Math.min(index * 40, 400)}ms`,
                      }}
                      className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                    >
                      <TableCell className="font-medium">{index + 1}</TableCell>
                      <TableCell className="max-w-[200px] truncate font-medium">
                        {client.clientName}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {client.clientTaxId}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        ${formatCurrency(client.totalSales)}
                      </TableCell>
                      <TableCell className="text-right">
                        {client.invoiceCount}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress
                            value={client.percentage}
                            className="h-2 flex-1"
                          />
                          <span className="w-14 text-right text-sm text-muted-foreground">
                            {client.percentage.toFixed(1)}%
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientSalesDashboardPage;
